import type { TabsItem } from '@protoss/ui';
import type { ScopeMeta } from '@protoss/shared';
import { useOutletContext } from 'react-router-dom';

import type {
  SalesDealQuestionnaireSummary,
  SalesDealSummary,
  SalesDealTemplateGroupSummary,
} from './services/salesApi';
import type {
  QuestionnaireAnswerMap,
  QuestionnaireFieldLookup,
} from './utils/questionnaire-field-visibility';

/**
 * Categories whose SOW is a static, placeholder-driven document generated
 * deterministically (see the sow profile's generation_mode='static'). These
 * categories get an estimate-only generate control (no "Generate all" - the SOW
 * is never part of a chat generation chain), a Generate SOW action on the
 * Contract tab gated on the contract terms, and the questionnaire wizard's
 * Previous/Next footer. Lives here (not DealWorkspace.tsx) so leaf components
 * like RegenerateMenuButton can read it without a circular import. Web-local on
 * purpose: no server code branches on the category (server behavior is
 * config-driven via the sow profile).
 */
/** One reason the Generate control is disabled, with optional detail lines (e.g. question labels). */
export type GenerateDisabledReason = { text: string; details?: string[] };

export const STATIC_SOW_WIZARD_CATEGORY_KEYS = new Set(['managed_services']);

/**
 * Which document sub-view is open in the right-rail "documents" panel.
 * Shared so the persistent DealWorkspace parent can own it and the center
 * panes can open documents without their own copy.
 */
export type ViewingDocState =
  | { kind: 'scope' }
  | { kind: 'systems-landscape' }
  | { kind: 'estimate' }
  | { kind: 'contract' }
  | { kind: 'resource-plan' }
  | { kind: 'sow' }
  | { kind: 'prioritization' }
  | { kind: 'upload'; docUuid: string }
  | { kind: 'documents' }
  | { kind: 'docs' };

/** Editable/displayable deal attribute (the "props" panel) - shared shape. */
export interface DealAttributeOption {
  id: number;
  valueKey: string;
  label: string;
  color: string | null;
  displayOrder: number;
}
export interface DealAttributeEntry {
  id: number;
  attributeKey: string;
  label: string;
  displayOrder: number;
  isSystem: boolean;
  currentValue: string | null;
  options: DealAttributeOption[];
}

/**
 * Everything the DealWorkspace parent route shares with its center-pane child
 * route (DealMain, shared by the view + edit routes) via React Router's Outlet context.
 *
 * The parent owns this state so the right rail (chat/docs/props) - which reads
 * the same deal/estimate data and answer-editing state - never remounts when
 * the user toggles view ↔ edit. Children read it via {@link useDealWorkspace}.
 *
 * The runtime context object may be a superset of this interface; children only
 * see the fields declared here.
 */
export interface DealWorkspaceOutletContext {
  // ── Identity / role ──────────────────────────────────────────────────────
  dealUuid: string | undefined;
  appUserUuid: string | undefined;
  salesModuleRole: string | null;
  isWorkspaceAdmin: boolean;

  // ── Deal + questionnaire data ────────────────────────────────────────────
  deal: SalesDealSummary | null;
  loadingDeal: boolean;
  dealError: string | null;
  /** Retry the deal load after a load failure (re-runs the parent's fetch effect). */
  retryLoadDeal: () => void;
  questionnaire: SalesDealQuestionnaireSummary | null;
  loadingQuestionnaire: boolean;
  questionnaireError: string | null;
  /** Retry the questionnaire load after a load failure. */
  retryLoadQuestionnaire: () => void;

  // ── Answer-editing state (owned by parent; read/written by edit form) ─────
  answers: QuestionnaireAnswerMap;
  persistedAnswers: QuestionnaireAnswerMap;
  fieldLookup: QuestionnaireFieldLookup;
  touchedFields: Set<string>;
  setTouchedFields: React.Dispatch<React.SetStateAction<Set<string>>>;
  changedFieldKeys: Set<string>;
  savableFieldKeys: Set<string>;
  saveError: string | null;
  handleAnswerChange: (fieldKey: string, value: unknown) => void;
  handlePersistAnswers: (question?: SalesDealTemplateGroupSummary) => Promise<boolean>;
  handleReloadQuestionnaire: () => Promise<void>;

  // ── Deal attributes ("props" panel) ──────────────────────────────────────
  dealAttributes: DealAttributeEntry[];
  handleOpenAttributesPane: () => void;
  handleUpdateDealTitle: (title: string) => void | Promise<void>;

  // ── Generation / enrichment / transcript ─────────────────────────────────
  /** "Generate all": estimate (+ resource plan) run, then the SOW run - two chat messages. */
  handleGenerateAll: () => void;
  /** Regenerate the estimate + resource plan only (own chat message; no SOW). */
  handleRegenerateEstimate: () => void;
  /** Regenerate the SOW only (own chat message; reads the saved estimate + resource plan). */
  handleRegenerateSow: () => void;
  /**
   * Static-SOW categories (managed_services): persist answers, generate the SOW directly
   * when the deal has none yet (deterministic, no chat run), and open the SOW tab. Drives
   * the questionnaire wizard footer's Preview SOW button.
   */
  handleWizardPreviewSow: () => Promise<void>;
  /** True while handleWizardPreviewSow is in flight (drives the footer button spinner). */
  previewingSow: boolean;
  /** Opens the enrich-company confirm dialog (mode picker; rendered by the parent). */
  openEnrichDialog: () => void;
  isAiRunActive: boolean;
  isEnrichmentRunning: boolean;
  /**
   * Number of AI-captured scope questions currently being applied + reloaded (0 when idle). While
   * > 0 the questionnaire's ad-hoc section renders skeleton rows so the user sees the questions are
   * being written/loaded after they click "Questions captured".
   */
  capturingAdhocCount: number;
  /**
   * Record an ad-hoc scope-item edit (status / work type / brief) as a PENDING change: it updates the
   * loaded questionnaire optimistically (so the strikethrough + brief reflect it immediately) and
   * arms the questionnaire Save button. It does NOT hit the server - the edit is persisted, with the
   * answers, when the user clicks Save (handlePersistAnswers flushes it). This makes ad-hoc edits
   * behave like the rest of the form (explicit save) instead of silently auto-saving.
   */
  onAdhocScopeChange: (fieldKey: string, meta: ScopeMeta) => void;
  /**
   * Immediately persist + live-sync the always-saved scope fields (Scope status + both priorities).
   * Unlike onAdhocScopeChange (staged until Save), this shallow-merges the patch into scope_meta
   * right away and updates the in-memory questionnaire so both the questionnaire card and the
   * Prioritization pane reflect it live.
   */
  commitAdhocScopeFields: (
    fieldKey: string,
    patch: Partial<
      Pick<
        ScopeMeta,
        | 'scopeStatus'
        | 'customerPriority'
        | 'recommendedPriority'
        | 'businessProcess'
        | 'workType'
        | 'inScope'
        | 'outOfScope'
        | 'complexityDrivers'
        | 'assumptions'
      >
    >
  ) => void;
  /** True when there are unsaved ad-hoc scope edits - keeps the Save button enabled for them. */
  hasPendingAdhocScope: boolean;
  /**
   * fieldKey of the AI-captured scope item currently focused across the split-view panes (null =
   * none). Clicking a Prioritization row or an AI-captured question sets it; both the Questionnaire
   * and the Prioritization pane scroll to + highlight the matching item so they stay in sync.
   */
  focusedAdhocFieldKey: string | null;
  setFocusedAdhocFieldKey: (fieldKey: string | null) => void;
  setShowTranscriptUploadPanel: React.Dispatch<React.SetStateAction<boolean>>;
  /** Generate/Regenerate button state for the edit header. */
  isGenerating: boolean;
  generateButtonDisabled: boolean;
  /** Why Generate is disabled, one entry per reason (empty when it isn't). */
  generateDisabledReasons: GenerateDisabledReason[];
  /**
   * COS-100 (docs/plans/72): AI-mode deal with no captured scope items - generation would be a
   * silent 0 h estimate, so Generate is disabled and explains why. Same predicate as the API gate.
   */
  noScopeCaptured: boolean;
  hasSavedEstimate: boolean;
  hasEstimateResult: boolean;
  /** Whether a SOW has been generated for this deal (gates the SOW regenerate button). */
  hasSavedSow: boolean;
  /** Whether the deal's product supports SOW generation (gates the SOW regenerate button). */
  isSowSupportedForDeal: boolean;
  /** Opens the "Generate all" regenerate-confirm dialog (rendered by the parent). */
  openRegenerateConfirm: () => void;
  /** Opens the estimate-only regenerate-confirm dialog (rendered by the parent). */
  openEstimateRegenerateConfirm: () => void;
  /** Opens the SOW-only regenerate-confirm dialog (rendered by the parent). */
  openSowRegenerateConfirm: () => void;

  // ── Main-canvas documents (estimate/RP/SOW render in main, not the rail) ──
  setViewingDoc: React.Dispatch<React.SetStateAction<ViewingDocState | null>>;
  secondaryDocumentSurface: React.ReactNode | null;
  setSecondaryViewingDoc: React.Dispatch<React.SetStateAction<ViewingDocState | null>>;
  /** Tab strip for the document switcher (Overview / Estimate / RP / SOW). */
  documentTabItems: TabsItem[];
  /** Active document tab value ('overview' when the deal form is shown). */
  activeDocValue: string;
  /** Switch the main canvas to a document tab (or 'overview' for the deal form). */
  switchDocMode: (value: string) => void;
  /** The active document viewer rendered in the main canvas; null on Overview. */
  documentSurface: React.ReactNode | null;
  /** Header-action toolbar for the active document (export, etc.); null on Overview. */
  documentHeaderActions: React.ReactNode | null;
  /**
   * Leading header status shown BEFORE the "Edit mode" badge (e.g. the Scope tab's autosave
   * "Saving…/Saved"). null when the active document has no such status.
   */
  documentHeaderStatus: React.ReactNode | null;
  /** Open the documents index (AI docs + uploaded transcripts) in the main canvas. */
  openDocumentsIndex: () => void;

  // ── Dirty-state guard (used by the edit "Back" action) ───────────────────
  confirmIfDirty: (key: string) => Promise<boolean>;
  dirtyKey: string;
}

/** Read the shared DealWorkspace context from inside a center-pane child route. */
export function useDealWorkspace(): DealWorkspaceOutletContext {
  return useOutletContext<DealWorkspaceOutletContext>();
}
